// Cache em memória dos indicadores por emissão, pra não refazer o fetch a cada troca
// de período no seletor: a API permite até 12 meses por chamada (MESES_MAX), então
// sempre buscamos essa janela inteira de uma vez e o resto é recorte client-side
// (ver findPeriodo/splitsAt em indicadoresModel). Um novo fetch só acontece quando o
// período pedido cai fora da janela já cacheada para aquela emissão.
import { postIndicadores } from './indicadoresApi';
import { findPeriodo } from './indicadoresModel';

export const MESES_MAX = 6;

const cache = new Map(); // emissaoId (ou "g:"+emissaoId pro preview de convidado) -> payload
const ck = (id, comoConvidado) => (comoConvidado ? `g:${id}` : id);

// Promise em voo por `periodo:emissaoId` — evita bulk duplicado se dois consumidores
// chamarem getIndicadoresBulkCached quase ao mesmo tempo antes da 1ª resposta chegar
// (StrictMode, Painel e Detalhe2 montando perto um do outro). Mesmo princípio de
// `_fullInFlight` em fluxoCaixaApi.js.
const _inFlight = new Map();

// Lista completa de emissões da conta (GET /v1/emissoes/) — sobrevive à navegação
// painel ↔ detalhe sem re-montar, evitando re-fetch desnecessário. A cobertura de
// indicadores por emissão vive só em `cache` (ver getIndicadoresBulkCached) — assim,
// mudar o referencial em Preferências não fica preso a uma foto antiga do bulk.
let _emissoesList = null;

export function getPainelCache() { return _emissoesList; }
export function setPainelCache(emissoesList) { _emissoesList = emissoesList; }

function subMonths(period, n) {
  let y = Math.floor(period / 100);
  let m = period % 100 - n;
  while (m <= 0) { m += 12; y--; }
  return y * 100 + m;
}

export async function getIndicadoresCached(emissaoId, periodo, comoConvidado) {
  const key = ck(emissaoId, comoConvidado);
  const cached = cache.get(key);
  if (cached && findPeriodo(cached, periodo)) return cached;
  const dataFim = parseInt(periodo, 10);
  const dataInicio = subMonths(dataFim, MESES_MAX - 1);
  const results = await postIndicadores([emissaoId], dataFim, dataInicio, comoConvidado);
  const data = (results && results[0]) || null;
  if (data) { cache.set(key, data); return data; }
  return cached || null;
}

// Igual a getIndicadoresCached, mas em lote: busca numa única chamada só os IDs cuja
// janela cacheada não cobre `periodo` — ex.: emissão recém-adicionada ao referencial em
// Preferências, que ainda não tem nada em `cache`. Mesma técnica de
// getSaldoDevedorCached/getCarteiraCached. Retorna { [emissaoId]: payload }, omitindo
// emissões sem dado algum ainda.
export async function getIndicadoresBulkCached(emissaoIds, periodo) {
  const key = id => `${periodo}:${id}`;
  const need = emissaoIds.filter(id => !findPeriodo(cache.get(id), periodo));
  const toFetch = need.filter(id => !_inFlight.has(key(id)));
  if (toFetch.length) {
    const dataFim = parseInt(periodo, 10);
    const dataInicio = subMonths(dataFim, MESES_MAX - 1);
    const promise = postIndicadores(toFetch, dataFim, dataInicio).then(results => {
      results.forEach(payload => { if (payload && payload.emissao_id != null) cache.set(payload.emissao_id, payload); });
      return results;
    });
    toFetch.forEach(id => _inFlight.set(key(id), promise));
    // .catch aqui só evita unhandledrejection do próprio .finally (não afeta `promise`,
    // que segue rejeitando normalmente pra quem já a aguarda via `joining` abaixo).
    promise.finally(() => { toFetch.forEach(id => _inFlight.delete(key(id))); }).catch(() => {});
  }
  const joining = [...new Set(need.filter(id => _inFlight.has(key(id))).map(id => _inFlight.get(key(id))))];
  if (joining.length) await Promise.all(joining);
  const out = {};
  emissaoIds.forEach(id => { const c = cache.get(id); if (c) out[id] = c; });
  return out;
}

// Leitura síncrona (sem fetch) do que já está em `cache` — usada só pra hidratar o
// estado inicial do Painel sem flicker ao remontar (painel ↔ detalhe).
export function peekIndicadoresBulkCached(emissaoIds, periodo) {
  const out = {};
  emissaoIds.forEach(id => { const c = cache.get(id); if (c && findPeriodo(c, periodo)) out[id] = c; });
  return out;
}

export function clearIndicadoresCache() {
  cache.clear();
  _inFlight.clear();
  _emissoesList = null;
}
